import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { SearchRewardsReservationsParams } from '~/services/RewardsService';
import { getOrderReservations, searchRewardsReservations } from '~/services/RewardsService';

import { rewardsQueryKeys } from './rewardsQueryKeys';

/**
 * Searches classic-reward reservations with filters + pagination (admin).
 *
 * Pass `null` to disable the query (e.g. before the dashboard's first render).
 * Returns the previous page's data while a new page is loading so the grid
 * doesn't flash empty between page changes.
 */
export function useSearchRewardsReservations(params: SearchRewardsReservationsParams | null) {
  return useQuery({
    queryKey: params
      ? rewardsQueryKeys.reservationsSearch(params as unknown as Record<string, unknown>)
      : [...rewardsQueryKeys.reservations(), 'disabled'],
    queryFn: async ({ signal }) => {
      if (!params) {
        return { reservations: [], total: 0, limit: 0, offset: 0 };
      }
      return await searchRewardsReservations(params, { signal });
    },
    enabled: params !== null,
    placeholderData: keepPreviousData,
  });
}

/** Lists every classic-reward reservation for one order (oldest first). */
export function useGetOrderReservations(orderId: string | null, options?: { includeInventory?: boolean }) {
  return useQuery({
    queryKey: orderId
      ? rewardsQueryKeys.reservationsByOrder(orderId)
      : [...rewardsQueryKeys.reservations(), 'order', 'disabled'],
    queryFn: async ({ signal }) => {
      if (!orderId) {
        return [];
      }
      return await getOrderReservations(orderId, { signal, includeInventory: options?.includeInventory });
    },
    enabled: Boolean(orderId),
  });
}
